import { Skeleton } from '@/components/ui/skeleton'

/**
 * Nav groups mirrored from the real sidebar (main / analytics / operations /
 * settings) so the placeholder has roughly the same vertical rhythm.
 */
const NAV_GROUPS = [4, 3, 3, 2]

/**
 * Whole-app placeholder shown while auth resolves and the router mounts —
 * before any route (and therefore any route-level skeleton) exists.
 *
 * Deliberately duplicates the shell layout rather than rendering the real
 * sidebar/header: those depend on router and auth context that isn't available
 * yet. It only has to match the geometry — sidebar width, header height, and
 * a content block — so the chrome doesn't jump when the app takes over.
 */
export function AppShellSkeleton() {
  return (
    <div className="flex h-screen w-full overflow-hidden" data-testid="app-shell-skeleton">
      {/* Sidebar — width matches SIDEBAR_WIDTH (14rem) in components/ui/sidebar. */}
      <div className="hidden w-56 flex-none flex-col gap-6 border-r bg-sidebar p-4 md:flex">
        <Skeleton className="h-8 w-32" />
        {NAV_GROUPS.map((items, group) => (
          <div key={group} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            {Array.from({ length: items }).map((_, item) => (
              <Skeleton key={item} className="h-8 w-full" />
            ))}
          </div>
        ))}
        <div className="mt-auto flex items-center gap-2">
          <Skeleton className="size-8 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header — matches SiteHeader's border + px-4 py-3 breadcrumb bar. */}
        <div className="flex items-center gap-3 border-b px-4 py-3 md:px-6">
          <Skeleton className="size-7" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="ml-auto h-8 w-28" />
        </div>

        <div className="flex-1 space-y-6 overflow-hidden p-6">
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-9 w-32" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 10 }).map((_, row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
